import type { FeaturedCategoryCard } from '../featuredCategoryCards'
import { CategoryTileImage } from './CategoryTileImage'

export interface FeaturedCategoriesProps {
  readonly cards: readonly FeaturedCategoryCard[]
  /**
   * UID-432 / ADR-0133 D6/D7: this component stays PRESENTATION-ONLY and
   * has no opinion on what a click does — the same contract `ShopByBrand`
   * and `ShopByCategory` both hold. `CatalogBrowsePage` owns navigation and
   * hands this straight to its existing `handleCategoryClick`. The value
   * passed is a LEAF category name (a featured card's `destinationCategory`
   * — the group's first present leaf), not the group name.
   */
  readonly onFeaturedGroupClick: (category: string) => void
}

const TINT_CLASSES = [
  'catalog-group-tint-0',
  'catalog-group-tint-1',
  'catalog-group-tint-2',
  'catalog-group-tint-3',
] as const

/**
 * The "Featured categories" panel (UID-432 / ADR-0133) — the left `1.5fr`
 * cell of `/catalog`'s `.catalog-bottom-row`, opposite `ShopByBrand`. Each
 * card represents a taxonomy GROUP (ADR-0133 D2): the group name, its
 * summed real `seriesCount` across present leaves, up to 3 leaf names as
 * tags, and the first present leaf's `imageUrl`. Every value on a card is
 * real, server-derived data reconciled by `featuredCategoryCards.ts`
 * (`deriveFeaturedCategoryCards`) — this component itself is purely
 * presentational and fabricates nothing.
 *
 * A click hands the card's `destinationCategory` (a single leaf) to
 * `onFeaturedGroupClick` — a card is a deep link into the group's first
 * leaf, not a view of the whole group (ADR-0133 D6), since no group-level
 * listing surface exists. The accessible name is "Explore <group>" (the
 * design source's own unused `exploreLabel`), not the bare group title,
 * since the group title alone would misdescribe a click that actually lands
 * on one leaf (ADR-0133 D7).
 */
export function FeaturedCategories({ cards, onFeaturedGroupClick }: FeaturedCategoriesProps) {
  return (
    <section aria-labelledby="featured-categories-heading" id="featured-categories">
      <h2 id="featured-categories-heading" className="catalog-featured-heading">
        Featured categories
      </h2>
      <div className="catalog-featured-grid">
        {cards.map((card, index) => (
          <button
            key={card.groupName}
            type="button"
            className="catalog-featured-tile"
            aria-label={`Explore ${card.groupName}`}
            onClick={() => onFeaturedGroupClick(card.destinationCategory)}
          >
            <CategoryTileImage
              imageUrl={card.imageUrl}
              tintClass={TINT_CLASSES[index % TINT_CLASSES.length] ?? TINT_CLASSES[0]}
              className="catalog-featured-tile-image"
            />
            <div className="catalog-featured-tile-body">
              <span className="catalog-featured-tile-name">{card.groupName}</span>
              {card.seriesCountLabel && (
                <span className="catalog-featured-tile-count">{card.seriesCountLabel}</span>
              )}
              {card.tags.length > 0 && (
                <div className="catalog-featured-tile-tags">
                  {card.tags.map((tag) => (
                    <span key={tag} className="catalog-featured-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </section>
  )
}
